import { ErrorAlert } from "#components/ErrorAlert"
import { PrivacyBlur } from "#components/privacy-blur"
import { useAccount } from "#contexts/AccountContext"
import { useCanPerformMutations } from "#hooks/use-can-perform-mutations"
import { useCurrencyFormatters } from "#hooks/use-currency-formatters"
import { useI18n } from "#hooks/use-i18n"
import {
  useCreatePartner,
  useDeletePartner,
  usePartners,
  useUpdatePartner,
} from "#hooks/use-partners"
import { slideUp } from "#lib/animations"
import { getUserFriendlyErrorMessage } from "#lib/errors"
import type { Partner } from "@home-finance-manager/shared/types/partners"
import {
  createCreatePartnerValidator,
  createUpdatePartnerValidator,
} from "@home-finance-manager/shared/validators/partners.validator.factory"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table"
import { motion } from "framer-motion"
import { Pencil, Trash2, User } from "lucide-react"
import React, { useCallback, useMemo, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { useSearchParams } from "react-router-dom"
import type { z } from "zod"
import { Dialog, DialogActions } from "../components/Dialog"
import { PageHeader } from "../components/page-header"
import { PartnersHeaderActions } from "../components/partners/PartnersHeaderActions"
import { Button } from "../components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card"
import { Input } from "../components/ui/input"
import { Label } from "../components/ui/label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table"
import { Tooltip, TooltipContent, TooltipTrigger } from "../components/ui/tooltip"
import { Body } from "../components/ui/typography"

const Partners: React.FC = () => {
  const { t } = useI18n()
  const { formatCurrency } = useCurrencyFormatters()
  const { currentAccount } = useAccount()
  const canPerformMutations = useCanPerformMutations()
  const { data: partners = [], isLoading: loading } = usePartners()
  const createPartner = useCreatePartner()
  const updatePartner = useUpdatePartner()
  const deletePartner = useDeletePartner()
  const [showAddPartner, setShowAddPartner] = useState(false)
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null)
  const [deletingPartner, setDeletingPartner] = useState<Partner | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()

  // Get sorting from URL
  const sorting = useMemo<SortingState>(() => {
    const sortParam = searchParams.get("sort")
    if (!sortParam) return []
    try {
      const parsed = JSON.parse(sortParam)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }, [searchParams])

  const setSorting = useCallback(
    (newSorting: SortingState | ((prev: SortingState) => SortingState)) => {
      setSearchParams((prev) => {
        const updatedSorting = typeof newSorting === "function" ? newSorting(sorting) : newSorting
        const newParams = new URLSearchParams(prev)
        if (updatedSorting.length === 0) {
          newParams.delete("sort")
        } else {
          newParams.set("sort", JSON.stringify(updatedSorting))
        }
        return newParams
      })
    },
    [setSearchParams, sorting]
  )

  const currentSchema = useMemo(
    () => (editingPartner ? createUpdatePartnerValidator(t) : createCreatePartnerValidator(t)),
    [editingPartner, t]
  )

  type PartnerFormData = z.input<typeof currentSchema>

  // React Hook Form setup
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PartnerFormData>({
    resolver: zodResolver(currentSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      grossMonthlySalary: 0,
      netMonthlySalary: 0,
    },
  })

  const onSubmit = async (data: PartnerFormData) => {
    if (!currentAccount) return

    setError(null)

    try {
      if (editingPartner) {
        await updatePartner.mutateAsync({ id: editingPartner.id, data })
      } else {
        const createData = {
          firstName: data.firstName || "",
          lastName: data.lastName || "",
          email: data.email || "",
          grossMonthlySalary: data.grossMonthlySalary ?? 0,
          netMonthlySalary: data.netMonthlySalary ?? 0,
        }
        await createPartner.mutateAsync(createData)
      }
      handleCloseDialog()
    } catch (err) {
      setError(getUserFriendlyErrorMessage(err))
    }
  }

  const handleEditPartner = (partner: Partner) => {
    setEditingPartner(partner)
    reset({
      firstName: partner.firstName || "",
      lastName: partner.lastName || "",
      email: partner.email || "",
      grossMonthlySalary: Number.parseFloat(String(partner.grossMonthlySalary)),
      netMonthlySalary: Number.parseFloat(String(partner.netMonthlySalary)),
    })
    setShowAddPartner(true)
  }

  const handleDeletePartner = async () => {
    if (!deletingPartner) return

    try {
      setError(null)
      await deletePartner.mutateAsync(deletingPartner.id)
      setDeletingPartner(null)
    } catch (err) {
      setError(getUserFriendlyErrorMessage(err))
      setDeletingPartner(null)
    }
  }

  const handleCloseDialog = () => {
    setShowAddPartner(false)
    setEditingPartner(null)
    setError(null)
    reset({
      firstName: "",
      lastName: "",
      email: "",
      grossMonthlySalary: 0,
      netMonthlySalary: 0,
    })
  }

  const columns = useMemo<ColumnDef<Partner>[]>(
    () => [
      {
        accessorKey: "name",
        header: t("pages.partners.table.name"),
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-secondary-foreground" />
            </div>
            <div>
              <div className="font-medium">
                {row.original.firstName} {row.original.lastName}
              </div>
              <div className="text-sm text-muted-foreground">{row.original.email}</div>
            </div>
          </div>
        ),
      },
      {
        accessorKey: "grossMonthlySalary",
        header: t("pages.partners.table.grossSalary"),
        cell: ({ row }) => (
          <div className="font-semibold">
            <PrivacyBlur>{formatCurrency(row.original.grossMonthlySalary as number)}</PrivacyBlur>
          </div>
        ),
      },
      {
        accessorKey: "netMonthlySalary",
        header: t("pages.partners.table.netSalary"),
        cell: ({ row }) => (
          <div className="font-semibold">
            <PrivacyBlur>{formatCurrency(row.original.netMonthlySalary as number)}</PrivacyBlur>
          </div>
        ),
      },
      {
        id: "actions",
        header: t("pages.partners.table.actions"),
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            {canPerformMutations ? (
              <>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleEditPartner(row.original)}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  variant="destructive"
                  size="icon"
                  onClick={() => setDeletingPartner(row.original)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button variant="outline" size="icon" disabled>
                        <Pencil className="w-4 h-4" />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t("pages.partners.tooltips.editDisabled")}</p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button variant="destructive" size="icon" disabled>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t("pages.partners.tooltips.deleteDisabled")}</p>
                  </TooltipContent>
                </Tooltip>
              </>
            )}
          </div>
        ),
      },
    ],
    [canPerformMutations, t]
  )

  const table = useReactTable({
    data: partners,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  })

  if (loading) {
    return (
      <div className="min-h-screen text-primary p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <Body className="text-secondary-foreground">{t("pages.partners.loading")}</Body>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 flex flex-col">
      <PageHeader
        title={t("pages.partners.pageTitle")}
        subtitle={t("pages.partners.pageSubtitle")}
        actions={
          <PartnersHeaderActions
            canAddPartner={partners.length < 2}
            onAddPartner={() => {
              setError(null)
              setShowAddPartner(true)
            }}
            isOwner={canPerformMutations}
          />
        }
      />

      {/* Partners Table */}
      <motion.div initial="hidden" animate="visible" variants={slideUp} className="flex-1 min-h-0">
        <Card className="flex-1 min-h-0 flex flex-col">
          <CardHeader className="flex-shrink-0">
            <CardTitle>{t("pages.partners.tableTitle")}</CardTitle>
            <CardDescription>{t("pages.partners.tableDescription")}</CardDescription>
          </CardHeader>
          <CardContent className="flex-1 min-h-0 overflow-auto">
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map((row) => (
                      <TableRow key={row.id} data-state={row.getIsSelected() && "selected"}>
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={columns.length} className="h-24 text-center">
                        {t("pages.partners.noPartners")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Add/Edit Partner Dialog */}
      <Dialog
        open={showAddPartner}
        onOpenChange={(open) => {
          if (!open) {
            handleCloseDialog()
          }
        }}
        title={editingPartner ? t("pages.partners.editTitle") : t("pages.partners.createTitle")}
        description={
          editingPartner
            ? t("pages.partners.editDescription")
            : t("pages.partners.createDescription")
        }
        size="md"
        footer={
          <DialogActions
            onCancel={handleCloseDialog}
            onConfirm={handleSubmit(onSubmit)}
            confirmText={
              editingPartner ? t("pages.partners.form.update") : t("pages.partners.form.add")
            }
            confirmVariant="default"
            cancelText={t("components.dialog.cancel")}
            isLoading={createPartner.isPending || updatePartner.isPending}
            isConfirmDisabled={!canPerformMutations}
            confirmTooltip={
              !canPerformMutations ? t("pages.partners.tooltips.actionDisabled") : undefined
            }
          />
        }
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 px-1">
          <ErrorAlert error={error} onDismiss={() => setError(null)} />

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">{t("pages.partners.form.firstName")}</Label>
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <Input
                    id="firstName"
                    type="text"
                    placeholder={t("pages.partners.form.firstNamePlaceholder")}
                    {...field}
                    className={errors.firstName ? "border-red-500" : ""}
                  />
                )}
              />
              {errors.firstName && (
                <p className="text-sm text-red-500">{errors.firstName.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName">{t("pages.partners.form.lastName")}</Label>
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <Input
                    id="lastName"
                    type="text"
                    placeholder={t("pages.partners.form.lastNamePlaceholder")}
                    {...field}
                    className={errors.lastName ? "border-red-500" : ""}
                  />
                )}
              />
              {errors.lastName && <p className="text-sm text-red-500">{errors.lastName.message}</p>}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">{t("pages.partners.form.email")}</Label>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <Input
                  id="email"
                  type="email"
                  placeholder={t("pages.partners.form.emailPlaceholder")}
                  {...field}
                  className={errors.email ? "border-red-500" : ""}
                />
              )}
            />
            {errors.email && <p className="text-sm text-red-500">{errors.email.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="grossMonthlySalary">{t("pages.partners.form.grossSalary")}</Label>
            <Controller
              name="grossMonthlySalary"
              control={control}
              render={({ field }) => (
                <Input
                  id="grossMonthlySalary"
                  type="number"
                  placeholder={t("pages.partners.form.grossSalaryPlaceholder")}
                  {...field}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                  min="0"
                  step="100"
                  className={errors.grossMonthlySalary ? "border-red-500" : ""}
                />
              )}
            />
            {errors.grossMonthlySalary && (
              <p className="text-sm text-red-500">{errors.grossMonthlySalary.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="netMonthlySalary">{t("pages.partners.form.netSalary")}</Label>
            <Controller
              name="netMonthlySalary"
              control={control}
              render={({ field }) => (
                <Input
                  id="netMonthlySalary"
                  type="number"
                  placeholder={t("pages.partners.form.netSalaryPlaceholder")}
                  {...field}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                  min="0"
                  step="100"
                  className={errors.netMonthlySalary ? "border-red-500" : ""}
                />
              )}
            />
            {errors.netMonthlySalary && (
              <p className="text-sm text-red-500">{errors.netMonthlySalary.message}</p>
            )}
          </div>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deletingPartner}
        onOpenChange={(open) => {
          if (!open) {
            setDeletingPartner(null)
          }
        }}
        title={t("pages.partners.deleteTitle")}
        description={t("pages.partners.deleteDescription")}
        size="sm"
        footer={
          <DialogActions
            onCancel={() => setDeletingPartner(null)}
            onConfirm={handleDeletePartner}
            confirmText={t("components.dialog.delete")}
            cancelText={t("components.dialog.cancel")}
            confirmVariant="destructive"
            isLoading={deletePartner.isPending}
            isConfirmDisabled={!canPerformMutations}
            confirmTooltip={
              !canPerformMutations ? t("pages.partners.tooltips.actionDisabled") : undefined
            }
          />
        }
      >
        <div className="space-y-2">
          <Body className="text-foreground">
            {t("pages.partners.deleteWarning", {
              firstName: deletingPartner?.firstName,
              lastName: deletingPartner?.lastName,
            })}
          </Body>
        </div>
      </Dialog>
    </div>
  )
}

export default Partners
